import { useState } from "react";
import { Alert, App, Button, Card, InputNumber, Select, Space, Typography } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { IntegrityCheckDto } from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { useIntegrityChecks, useVerifyIntegrity } from "../../api/admin";
import { Pill, fmtDT } from "../pills";
import { ResponsiveList } from "../ResponsiveList";
import { Sensitive } from "../Sensitive";

/** Amministrazione → Integrità: verifica della catena di hash del registro modifiche e degli sha256 delle evidenze a campione. */
export function IntegrityTab() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const checks = useIntegrityChecks();
  const verify = useVerifyIntegrity();
  const [sample, setSample] = useState(20);
  const [since, setSince] = useState<"start" | "checkpoint">("start");
  const [last, setLast] = useState<IntegrityCheckDto | null>(null);
  const go = async () => {
    try {
      setLast(await verify.mutateAsync({ sample, since }));
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));
    }
  };
  const res = (c: IntegrityCheckDto) =>
    c.ok ? (
      <Alert type="success" showIcon message={t("d_intOk", { rows: c.chainRows, n: c.sampled })} />
    ) : (
      <Alert
        type="error"
        showIcon
        message={t("d_intKo")}
        description={
          <div>
            {c.firstBrokenId != null && <div>{t("d_intBroken", { id: c.firstBrokenId })}</div>}
            {c.mismatches.map((m) => (
              <div key={m.versionId}>{t("d_intMismatch", { name: m.name, v: m.v, reason: t(`d_intReason_${m.reason}`) })}</div>
            ))}
          </div>
        }
      />
    );

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <Card title={t("d_intTitle")}>
        <Typography.Paragraph type="secondary" style={{ fontSize: 13 }}>{t("d_intHelp")}</Typography.Paragraph>
        <Space wrap align="end" style={{ marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{t("d_intSample")}</div>
            <InputNumber min={0} max={500} value={sample} onChange={(v) => setSample(v ?? 20)} />
          </div>
          <div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{t("d_intScope")}</div>
            <Select style={{ width: 240, maxWidth: "100%" }} value={since} onChange={setSince} options={[{ value: "start", label: t("d_intScopeStart") }, { value: "checkpoint", label: t("d_intScopeCheckpoint") }]} />
          </div>
          <Button type="primary" icon={<SafetyCertificateOutlined />} loading={verify.isPending} onClick={go}>{t("d_intRun")}</Button>
        </Space>
        {last && res(last)}
        {last && last.unhashed > 0 && <Typography.Paragraph type="secondary" style={{ marginTop: 8, fontSize: 12 }}>{t("d_intUnhashed", { n: last.unhashed })}</Typography.Paragraph>}
      </Card>
      <Card title={t("d_intHistory")} styles={{ body: { padding: 0 } }}>
        <ResponsiveList<IntegrityCheckDto>
          size="small"
          rowKey="id"
          loading={checks.isLoading}
          dataSource={checks.data ?? []}
          pageSize={15}
          card={{ code: (c) => fmtDT(c.ranAt), title: (c) => <Sensitive>{c.userName}</Sensitive>, tags: (c) => <Pill tone={c.ok ? "ok" : "bad"}>{c.ok ? t("d_intPass") : t("d_intFail")}</Pill>, fields: (c) => [[t("d_intRows"), String(c.chainRows)], [t("d_intSampled"), String(c.sampled)]] }}
          columns={[
            { title: t("date"), dataIndex: "ranAt", render: (v: string) => fmtDT(v) },
            { title: t("user"), dataIndex: "userName", render: (v: string) => <Sensitive>{v}</Sensitive> },
            { title: t("d_intScope"), dataIndex: "scope", render: (v: string) => (v === "checkpoint" ? t("d_intScopeCheckpoint") : t("d_intScopeStart")) },
            { title: t("d_intRows"), dataIndex: "chainRows", align: "right" as const },
            { title: t("d_intSampled"), dataIndex: "sampled", align: "right" as const },
            { title: t("d_intResult"), render: (_, c) => <Pill tone={c.ok ? "ok" : "bad"}>{c.ok ? t("d_intPass") : c.firstBrokenId != null ? t("d_intBroken", { id: c.firstBrokenId }) : t("d_intFail")}</Pill> },
          ]}
        />
      </Card>
    </div>
  );
}
